import { Component, OnInit, Inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { FdFieldConfigs } from '../../../shared/fd-form-components/fd-form-components.module';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../shared/service/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { LoadingService } from '../../../shared/service/loading.service';
import { Ga3PasswordValidator, PasswordValidator } from '../../../shared/validators/password-validator';
import { CompareValidator } from '../../../shared/validators/compare-validator';
import { CompareTypeEnum } from '../../../shared/enums/compare-type.enum';
import { FdInputConfig, InputType } from '../../../shared/fd-form-components/fd-input/fd-input.component';
import { UserCreateStepEnum } from '../../../users/components/add-user/add-user.component';
import { PasswordModel } from '../../../shared/models/password.model';
import { PasswordPolicy, PasswordRecoveryContextResponse } from '../../../shared/models/response/password-recovery-context.model';
import { Messages } from '../../../shared/messages/messages';
import { DialogService } from '../../../shared/service/dialog.service';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../shared/service/error.service';
import { Item } from '../../../shared/fd-form-components/fd-select/fd-select.component';
import { RequiredIfValidator } from '../../../shared/validators/required-if-validator';
import { LoginService } from '../../services/login.service';
import { LoginTypeEnum } from '../../enums/login-type.enum';
import { environment } from '../../../../environments/environment';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'app-password-reset',
    templateUrl: './clientpass-reset.component.html',
    styleUrls: ['./clientpass-reset.component.scss'],
    standalone: false
})
export class ClientpassResetComponent implements OnInit {

  formGroup: UntypedFormGroup;
  sentTokenMessage: string;
  mailCode: string;
  fields: FdFieldConfigs;
  currentStep: UserCreateStepEnum = UserCreateStepEnum.DATA_CREATE;

  isGa3Flow = false;
  passwordPolicy: PasswordPolicy | null = null;
  correlationId: string;
  maskedContact: string;
  tokenChannelOptions: Item[] = [
    {
      value: 'SMS',
      selected: true,
      label: 'SMS'
    },
    {
      value: 'EMAIL',
      label: 'E-mail'
    }
  ]

  loginActive = true;
  loginUrl;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private loginService: LoginService,
    protected authService: AuthService,
    protected matDialog: MatDialog,
    @Inject(DOCUMENT) private document: any,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private router: Router) {

    const navigationState = this.router.getCurrentNavigation()?.extras?.state as any;
    if (navigationState?.flow === 'GA3') {
      this.isGa3Flow = true;
      this.passwordPolicy = 'GA3';
      this.correlationId = navigationState.correlationId;
      this.maskedContact = navigationState.maskedContact;
    }

    this.route.queryParams.subscribe(params => {
      const mailCode = params['mail-code'];
      const loginTypeParam = params['type'];

      if(!loginTypeParam){
        this.loginUrl =  environment.ADMIN_URL + 'login';
      } else if (loginTypeParam === LoginTypeEnum.CONCILIATOR ) {
        this.loginUrl = environment.CONCILIATOR_URL + 'login'
      } else{
        this.loginUrl = loginTypeParam === LoginTypeEnum.ADMIN ? environment.ADMIN_URL + 'login'  : environment.ACCREDITATION_URL + 'login';
      }

      if(!this.isGa3Flow && !mailCode){
        this.dialogService.openDialog(Messages.NO_RECOVERY_TOKEN_PROVIDED, () => this.returnToLogin())
      }

      this.mailCode = mailCode;
    });
  }

  ngOnInit() {
    this.loadingService.hide();
    this.startForms();

    if (this.isGa3Flow) {
      this.sentTokenMessage = `Um código foi enviado para ${this.maskedContact || 'o contato cadastrado'}`;
      this.currentStep = UserCreateStepEnum.TOKEN_VALIDATE;
    }
  }

  returnToLogin() {
    this.document.location.href = this.loginUrl;
  }

  resendToken() {
    this.loadingService.show();

    if (this.isGa3Flow) {
      this.loginService.resendGa3PasswordCode(this.correlationId)
        .pipe(finalize(() => this.loadingService.hide()))
        .subscribe(response => {
          this.correlationId = response?.correlationId || this.correlationId;
          this.maskedContact = response?.maskedContact || this.maskedContact;
          this.sentTokenMessage = `Um código foi enviado para ${this.maskedContact || 'o contato cadastrado'}`;
          this.currentStep = UserCreateStepEnum.TOKEN_VALIDATE;
          this.dialogService.openDialog(Messages.TOKEN_SUCCESSFULLY_RESENT);
        }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR));
      return;
    }

    if (!this.formGroup.value.tokenChannel) {
      return;
    };

    this.loginService.startRecoverFlow(this.mailCode, this.formGroup.value.tokenChannel)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(response => {
        if (!this.applyRecoveryContext(response)) {
          return;
        }

        if(response && response.message){
          this.sentTokenMessage = response.message;
        }

        this.currentStep = UserCreateStepEnum.TOKEN_VALIDATE;
        this.dialogService.openDialog(Messages.TOKEN_SUCCESSFULLY_RESENT);
      }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR));
  }

  get isPasswordStep() {
    return this.currentStep === UserCreateStepEnum.PASSWORD_CREATE;
  }

  get isTkStep() {
    return this.currentStep === UserCreateStepEnum.TOKEN_VALIDATE;
  }

  get isInitialStep() {
    return this.currentStep === UserCreateStepEnum.DATA_CREATE;
  }

  sendToken() {

    if (!this.formGroup.value.tokenChannel) {
      return;
    };

    this.loginService.startRecoverFlow(this.mailCode, this.formGroup.value.tokenChannel)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(response => {
        if (!this.applyRecoveryContext(response)) {
          return;
        }

        if(response && response.message){
          this.sentTokenMessage = response.message;
        }

        this.currentStep = UserCreateStepEnum.TOKEN_VALIDATE;
      }, (err: HttpErrorResponse) => {

        if(err.status===409)
        {
          this.dialogService.openDialogRunFunction(Messages.EDIT_SAVE_ERROR_CONFLIT, ()=>this.returnToLoginCallBack(this.loginUrl));
        }
        else
        {
          this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR);
        }
      });
  }

    private returnToLoginCallBack(value: any) {

    this.document.location.href = value;
  }

  save(): void {
    if (!this.formGroup.valid) {
      return;
    }

    this.loadingService.show();

    if (this.currentStep === UserCreateStepEnum.TOKEN_VALIDATE) {
      this.validateToken();
    }
    else if (this.currentStep === UserCreateStepEnum.PASSWORD_CREATE) {
      this.createPassword();
    }
    else {
      this.sendToken();
    }
  }

  validateToken() {

    if (!this.hasPasswordPolicy()) {
      return;
    }

    this.loadingService.show();

    let token = this.formGroup.value.token;

    if (!token) {
      this.dialogService.openDialog(Messages.DATA_REQUIRED_ERROR);
      return;
    }

    if (this.isGa3Flow) {
      this.loadingService.hide();
      this.currentStep = UserCreateStepEnum.PASSWORD_CREATE;
      return;
    }

    this.loginService.validateTokenForUserRecovery(this.mailCode, token)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.currentStep = UserCreateStepEnum.PASSWORD_CREATE;
      }, (err: HttpErrorResponse) => this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR));

  }

  returnOnlyDigits(value: string) {
    if (value) {
      return value.replace(/[^0-9]/g, '');
    }
  }

  createPassword() {

    if (!this.hasPasswordPolicy()) {
      return;
    }

    let data: PasswordModel = {
      confirmPassword: this.formGroup.value.passwordConfirmation,
      password: this.formGroup.value.password,
      token: this.formGroup.value.token,
      correlationId: this.isGa3Flow ? this.correlationId : undefined
    }

    if (!data.confirmPassword ||
      !data.password ||
      !data.token ||
      !this.formGroup.valid) {
      this.dialogService.openDialog(Messages.DATA_REQUIRED_ERROR);
      return;
    }

    this.loginService.createPasswordForUserRecovery(this.isGa3Flow ? undefined : this.mailCode, data)
      .pipe(finalize(() => this.loadingService.hide()))
      .subscribe(() => {
        this.dialogService.openDialog(Messages.EDIT_SAVE_SUCCESS, () => this.returnToLogin());
      }, (err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 422) {
          this.dialogService.openDialog(Messages.RECOVERY_SESSION_EXPIRED, () => this.returnToLogin());
          return;
        }
        this.errorService.handleXHRError(err, Messages.EDIT_SAVE_ERROR);
      });
  }

  handlerMFAPaste(token) {
    this.formGroup.controls['token'].setValue('');
    this.formGroup.controls['token'].setValue(token);
    this.save();
  }

  startForms(): void {

    this.formGroup = this.formBuilder.group({
      password: ['', [
        RequiredIfValidator(() => this.currentStep === UserCreateStepEnum.PASSWORD_CREATE),
        ...(this.passwordPolicy ? [this.passwordPolicy === 'GA3' ? Ga3PasswordValidator : PasswordValidator] : [])
      ]],
      passwordConfirmation: [''],
      tokenChannel: ['', [RequiredIfValidator(() => !this.isGa3Flow)]],
      token: ['', [RequiredIfValidator(() => this.currentStep === UserCreateStepEnum.TOKEN_VALIDATE)]],
    });

    this.fields = {
      password: this.createPasswordFieldConfig(),
      passwordConfirmation: {
        label: 'Confirme a senha',
        controlName: 'passwordConfirmation',
        subscriptSizing: 'dynamic',
        type: InputType.PASSWORD,
        messages: {
          required: 'Informe uma senha válida',
          equal: 'As senhas não conferem'
        }
      },
      token: {
        label: 'Token',
        controlName: 'token',
        messages: {
          required: 'Informe o token'
        }
      },
      tokenChannel: {
        controlName: 'tokenChannel',
        items: this.tokenChannelOptions,
        messages: {
          required: 'Informe se a pessoa é politicamente exposta'
        }
      },
    };

    this.formGroup.controls.passwordConfirmation.setValidators(
      [
        RequiredIfValidator(() => this.currentStep === UserCreateStepEnum.PASSWORD_CREATE),
        CompareValidator(this.formGroup.controls.password, CompareTypeEnum.EQUAL)
      ]
    )
  }

  private applyRecoveryContext(response: PasswordRecoveryContextResponse): boolean {
    const policy = response?.data?.passwordPolicy;
    const operation = response?.data?.operation;
    if (response?.error || (policy !== 'GA1' && policy !== 'GA3') ||
        (operation !== 'FIRST_ACCESS' && operation !== 'PASSWORD_RECOVERY')) {
      this.passwordPolicy = null;
      return this.hasPasswordPolicy();
    }

    this.passwordPolicy = policy;
    this.fields.password = this.createPasswordFieldConfig();
    this.formGroup.controls.password.setValidators([
      RequiredIfValidator(() => this.currentStep === UserCreateStepEnum.PASSWORD_CREATE),
      policy === 'GA3' ? Ga3PasswordValidator : PasswordValidator
    ]);
    this.formGroup.controls.password.updateValueAndValidity();
    return true;
  }

  private hasPasswordPolicy(): boolean {
    if (!this.passwordPolicy) {
      this.dialogService.openDialogWithMessage(
        'Não foi possível identificar as regras de senha. Solicite novamente o envio do código.'
      );
      return false;
    }
    return true;
  }

  private createPasswordFieldConfig(): FdInputConfig {
    return {
      label: 'Nova senha',
      controlName: 'password',
      subscriptSizing: 'dynamic',
      hint: this.passwordPolicy === 'GA3'
        ? 'Sua senha deve atender aos seguintes critérios: não deve conter sequências idênticas (ex.: aaa, 111); não deve conter e-mail, nome ou sobrenome; mínimo de 12 e máximo de 64 caracteres; deve conter letras maiúsculas, minúsculas, números e caracteres especiais (ex.: !@#$%^&*); não deve conter espaços.'
        : this.passwordPolicy === 'GA1'
          ? 'A senha deve conter pelo menos 9 caracteres, 1 letra minúscula, 1 letra maiúscula, 1 número e 1 caracter especial, que pode ser um dos seguintes: @#$%^&+='
          : undefined,
      maxLength: this.passwordPolicy === 'GA3' ? 64 : undefined,
      type: InputType.PASSWORD,
      messages: this.passwordPolicy === 'GA3'
        ? {
          required: 'Informe a senha',
          ga3MinLength: 'A senha deve ter no mínimo 12 caracteres',
          ga3MaxLength: 'A senha deve ter no máximo 64 caracteres',
          ga3Whitespace: 'A senha não deve conter espaços',
          ga3RepeatedCharacters: 'A senha não deve conter sequências de 3 caracteres idênticos',
          ga3LowercaseMatch: 'A senha deve conter pelo menos 1 letra minúscula',
          ga3UppercaseMatch: 'A senha deve conter pelo menos 1 letra maiúscula',
          ga3NumberMatch: 'A senha deve conter pelo menos 1 número',
          ga3SpecialCharactersMatch: 'A senha deve conter pelo menos 1 caracter especial'
        }
        : {
          required: 'Informe a senha',
          specialCharsMatch: 'A senha deve conter pelo menos 1 caracter especial (@#$%^&+=)',
          numberMatch: 'A senha deve conter pelo menos 1 número',
          lengthMatch: 'A senha deve ter no mínimo 9 caracteres',
          lowerCaseMatch: 'A senha deve conter pelo menos 1 letra minúscula',
          upperCaseMatch: 'A senha deve conter pelo menos 1 letra maiúscula'
        }
    };
  }

}
